// Thanks to:
// https://github.com/vueuse/vueuse/issues/1592#issuecomment-1381020982
import type {
  UseTimeAgoMessages,
  UseTimeAgoUnitNamesDefault,
} from '@vueuse/core';
import { i18n } from '~/common/modules/i18n';

export function useLocaleTimeAgo(date: Date) {
  const { t } = i18n;

  const I18N_MESSAGES: UseTimeAgoMessages<UseTimeAgoUnitNamesDefault> = {
    justNow: t('common.timeAgo.just-now'),
    past: (n) => (n.match(/\d/) ? t('common.timeAgo.ago', [n]) : n),
    future: (n) => (n.match(/\d/) ? t('common.timeAgo.in', [n]) : n),
    month: (n, past) =>
      n === 1
        ? past
          ? t('common.timeAgo.last-month')
          : t('common.timeAgo.next-month')
        : `${n} ${t('common.timeAgo.month', n)}`,
    year: (n, past) =>
      n === 1
        ? past
          ? t('common.timeAgo.last-year')
          : t('common.timeAgo.next-year')
        : `${n} ${t('common.timeAgo.year', n)}`,
    day: (n, past) =>
      n === 1
        ? past
          ? t('common.timeAgo.yesterday')
          : t('common.timeAgo.tomorrow')
        : `${n} ${t('common.timeAgo.day', n)}`,
    week: (n, past) =>
      n === 1
        ? past
          ? t('common.timeAgo.last-week')
          : t('common.timeAgo.next-week')
        : `${n} ${t('common.timeAgo.week', n)}`,
    hour: (n) => `${n} ${t('common.timeAgo.hour', n)}`,
    minute: (n) => `${n} ${t('common.timeAgo.minute', n)}`,
    second: (n) => `${n} ${t('common.timeAgo.second', n)}`,
    invalid: '',
  };

  return useTimeAgo(date, {
    fullDateFormatter: (date: Date) => date.toLocaleDateString(),
    messages: I18N_MESSAGES,
  });
}

export function useConvertSeconds(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const hourString = hours > 0 ? `${hours} hr` : '';
  const minuteString = minutes > 0 ? `${minutes} min` : '';

  if (hours > 0) return `${hourString} ${minuteString || '0 min'}`;
  else return `${minuteString}`;
}

export function useFormatDateShort(dateString: string): string {
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };
  if (!dateString) {
    return '';
  }
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, options);
}

export function useFormatString(input: string): string {
  const formattedString = input
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toLowerCase());
  return (
    formattedString.charAt(0).toUpperCase() +
    formattedString.slice(1).toLowerCase()
  );
}
